import moment, { Moment } from "moment";

export enum Method360 {
	US = 0,
	EU = 1,
	US_NASD = 2
}

export class PrimaryTimeUtils {

	/**
	 * Calculates the nubmer of days between two dates based on a 360-day year, using the NASD
	 * method (30US/360) or European method (30E/360).
	 *
	 * Reference:
	 * https://en.wikipedia.org/wiki/360-day_calendar
	 */
	public static daysDifference360(start: Moment, end: Moment, method: Method360 = Method360.US_NASD) {

		let startDay = start.toDate().getUTCDate();
		let endDay = end.toDate().getUTCDate();

		if (method === Method360.EU) {
			// If either date A or B falls on the 31st of the month, that date will be changed to the 30th.
			startDay = Math.min(startDay, 30);
			endDay = Math.min(endDay, 30);
		} 
		
		else {
			/**
			 * If both date A and B fall on the last day of February, then date B will be changed to
			 * the 30th (unless preserving Excel compatibility)
			 */
			const isStartLastFebDay = PrimaryTimeUtils.isLastDayInFebruary(start);
			const isEndLastFebDay = PrimaryTimeUtils.isLastDayInFebruary(end);
			if (method === Method360.US_NASD && isStartLastFebDay && isEndLastFebDay) {
				endDay = 30;
			}

			/**
			 * If date A falls on the 31st of a month or last day of February, then date A will be changed
			 * to the 30th.
			 */
			if (isStartLastFebDay || startDay === 31) {
				startDay = 30;
			}

			/**
			 * If date A falls on the 30th of a month after applying (2) above and date B falls on the
			 * 31st of a month, then date B will be changed to the 30th.
			 */
			if (startDay === 30 && endDay === 31) {
				endDay = 30;
			}
		}
		
		return ((end.toDate().getUTCFullYear() - start.toDate().getUTCFullYear()) * 360)
			+ ((end.toDate().getUTCMonth() - start.toDate().getUTCMonth()) * 30)
			+ (endDay - startDay);
	}

	/**
	 * Converts a YTM (years to maturity) value into a Moment date,
	 * using the 30/360 (NASD or EU) day-count convention.
	 *
	 * @param {Moment} deliveryDate - The bond's delivery/start date.
	 * @param {number} ytm - The years-to-maturity value.
	 * @param {Method360} [method=Method360.US_NASD] - The 30/360 calculation method to apply.
	 * @returns {Moment} The corresponding date for the given YTM.
	 */
	public static ytmToMoment360(
		deliveryDate: Moment,
		ytm: number,
		method: Method360 = Method360.US_NASD
	): Moment {
		// Split YTM into years and fractional year portion
		const totalDays = Math.round(ytm * 360); // 360-day convention
		const years = Math.floor(totalDays / 360);
		const remainingDays = totalDays % 360;
		const months = Math.floor(remainingDays / 30);
		const days = remainingDays % 30;

		// Start from delivery date and add 30/360-based increments
		const result = deliveryDate.clone().add(years, "years").add(months, "months").add(days, "days");

		// Adjust according to the 30/360 method rules
		// (to remain consistent with daysDifference360 behavior)
		if (method === Method360.EU) {
			// Clamp to 30 if it lands on the 31st
			if (result.date() === 31) result.date(30);
		} else if (method === Method360.US_NASD) {
			const isDeliveryLastFeb = PrimaryTimeUtils.isLastDayInFebruary(deliveryDate);
			if (isDeliveryLastFeb && result.month() === 1 && result.date() === 28) {
			result.date(30);
			}
			if (result.date() === 31) result.date(30);
		}

		return result;
	}


	/**
	 * Returns true if it's the last day in February
	 */
	private static isLastDayInFebruary(value: Moment): boolean {
		if (value.month() === 1) {
			const lastDayOfFebruary = value.clone().endOf('month');
			return value.isSame(lastDayOfFebruary, 'day');
		}
		return false;
	}

	/**
     * Returns the coupon period start, a.k.a. the first date in which the coupon start to generate
     * interest. 
     */
	public static getCouponPeriodStart(settlement: Moment, maturity: Moment): Moment {
		let periodStart = moment(maturity);
		periodStart.year(moment(settlement).year());

		while (periodStart.isAfter(settlement)) {
			periodStart.subtract(6, "months");
		}
		
		return periodStart;
	}

}
